type AttributionValue = string | number | boolean | null | undefined;

export type AcquisitionAttribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  landingPath?: string;
  referrer?: string;
  capturedAt?: string;
};

const STORAGE_KEY = 'kulum_acquisition_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

function readStoredAttribution(): AcquisitionAttribution | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AcquisitionAttribution;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredAttribution(attribution: AcquisitionAttribution): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Storage can be unavailable in strict privacy modes.
  }
}

export function captureAcquisitionAttribution(): void {
  if (typeof window === 'undefined') return;

  const searchParams = new URLSearchParams(window.location.search);
  const hasUtm = UTM_KEYS.some((key) => searchParams.has(key));
  const existing = readStoredAttribution();

  if (!hasUtm && existing) {
    return;
  }

  const attribution: AcquisitionAttribution = {
    ...existing,
    landingPath: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || existing?.referrer,
    capturedAt: new Date().toISOString(),
  };

  if (hasUtm) {
    attribution.utmSource = searchParams.get('utm_source') || undefined;
    attribution.utmMedium = searchParams.get('utm_medium') || undefined;
    attribution.utmCampaign = searchParams.get('utm_campaign') || undefined;
    attribution.utmContent = searchParams.get('utm_content') || undefined;
    attribution.utmTerm = searchParams.get('utm_term') || undefined;
  }

  writeStoredAttribution(attribution);
}

export function getAcquisitionAttribution(): AcquisitionAttribution | null {
  return readStoredAttribution();
}

export function getAcquisitionEventParams(): Record<string, AttributionValue> {
  const attribution = getAcquisitionAttribution();
  if (!attribution) return {};

  return {
    acquisition_source: attribution.utmSource,
    acquisition_medium: attribution.utmMedium,
    acquisition_campaign: attribution.utmCampaign,
    acquisition_content: attribution.utmContent,
    acquisition_term: attribution.utmTerm,
    acquisition_landing_path: attribution.landingPath,
  };
}
